import { SejourSummary } from './sejour-summary.js';
import { QuantityControls } from './quantity-controls.js';
import { ParticipantsManager } from './participants-manager.js';
import { OptionsValidator } from './options-validator.js';

class GFormSejour {
    constructor() {
        this.formWrapper = document.querySelector('.gform_wrapper');
        if (!this.formWrapper) return;

        this.formId = this.formWrapper.id?.match(/gform_wrapper_(\d+)/)?.[1];
        if (!this.formId) return;

        this.config = {
            depositPercentage: 0.5
        };

        this.prices = {
            participant: 0,
            adultPension: 0,
            childPension: 0,
            transfer: 0
        };

        this.state = {
            participants: 1,
            accommodation: null,
            accommodationPrice: 0,
            adultPensions: 0,
            childPensions: 0,
            transferCount: 0
        };

        // Récupérer la page actuelle depuis le DOM
        this.currentPage = this.getInitialPageNumber();
        this.previousPage = parseInt(sessionStorage.getItem('gform_previous_page') || '1');

        // Vérifier si on revient en arrière au chargement
        const storedPage = parseInt(sessionStorage.getItem('gform_current_page') || '1');
        console.log('Initial state - storedPage:', storedPage, 'currentPage:', this.currentPage);

        if (this.currentPage < storedPage) {
            console.log('Detected going back on page load! From', storedPage, 'to', this.currentPage);
            this.resetStepsAfterOnLoad(this.currentPage);
        }

        // Sauvegarder la page actuelle
        sessionStorage.setItem('gform_previous_page', storedPage.toString());
        sessionStorage.setItem('gform_current_page', this.currentPage.toString());

        this.init();
    }

    getInitialPageNumber() {
        const pages = this.formWrapper.querySelectorAll('.gform_page');
        for (const page of pages) {
            if (!page.style.display || page.style.display !== 'none') {
                const pageId = page.id;
                const match = pageId.match(/gform_page_\d+_(\d+)/);
                if (match) {
                    return parseInt(match[1]);
                }
            }
        }
        return 1;
    }

    resetStepsAfterOnLoad(pageNumber) {
        console.log('resetStepsAfterOnLoad called with pageNumber:', pageNumber);

        // Reset étape 3 (pensions et transfert) si on revient à l'étape 1 ou 2
        if (pageNumber <= 2) {
            console.log('Resetting pensions and transfer (step 3)');
            // Reset pensions via classes CSS custom
            const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
            const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');
            const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');

            if (adultField) {
                const input = adultField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
            if (childField) {
                const input = childField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
            if (transferField) {
                const input = transferField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
        }
    }

    init() {
        console.log('GFormSejour initialized - formId:', this.formId);

        this.extractPricesFromForm();
        this.injectReserveBanner();
        this.injectSejourSummary();
        this.injectPaymentInfo();
        this.enhanceAccommodationCards();

        this.summary = new SejourSummary(this);
        this.quantityControls = new QuantityControls(this);
        this.participantsManager = new ParticipantsManager(this);
        this.optionsValidator = new OptionsValidator(this);

        this.bindEvents();
        this.updateState();

        console.log('GFormSejour ready - currentPage:', this.currentPage);
    }

    extractPricesFromForm() {
        // Utiliser les classes CSS custom pour trouver les champs
        const participantField = this.formWrapper.querySelector('.gf-sejour-participants');
        if (participantField) {
            const priceInput = participantField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.participant = this.parsePriceString(priceInput.value);
            }
        }

        const adultPensionField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        if (adultPensionField) {
            const priceInput = adultPensionField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.adultPension = this.parsePriceString(priceInput.value);
            }
        }

        const childPensionField = this.formWrapper.querySelector('.gf-sejour-pension-child');
        if (childPensionField) {
            const priceInput = childPensionField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.childPension = this.parsePriceString(priceInput.value);
            }
        }

        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const priceInput = transferField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.transfer = this.parsePriceString(priceInput.value);
            }
        }
    }

    parsePriceString(priceStr) {
        if (!priceStr) return 0;
        // Format européen : 1.050,00 € -> enlever les points (séparateur milliers), remplacer virgule par point
        const cleaned = priceStr
            .replace(/[^\d,.-]/g, '')  // Garder uniquement chiffres, virgule, point, tiret
            .replace(/\./g, '')         // Enlever les points (séparateur milliers)
            .replace(',', '.');          // Remplacer virgule par point (décimales)
        return parseFloat(cleaned) || 0;
    }

    injectReserveBanner() {
        const existingBanner = document.querySelector('.gf-reserve-banner');
        if (existingBanner) return;

        const bannerHTML = `<div class="gf-reserve-banner">Réserver</div>`;
        this.formWrapper.insertAdjacentHTML('beforebegin', bannerHTML);
    }

    injectSejourSummary() {
        const existingSummary = this.formWrapper.querySelector('.gf-sejour-summary');
        if (existingSummary) return;

        // Récupérer les infos du séjour depuis le champ caché
        const infoSejourField = this.formWrapper.querySelector('.gf-infos-sejour p');
        const infoSejourText = infoSejourField ? infoSejourField.innerHTML : '';

        const summaryHTML = `
            <div class="gf-sejour-summary">
                <h3 class="gf-sejour-summary__title">Votre séjour</h3>
                <div class="gf-sejour-summary__subtitle">${infoSejourText}</div>
                <div class="gf-sejour-summary__items">
                    <div class="gf-sejour-summary__item" data-summary="participants">
                        <div class="gf-sejour-summary__label">
                            Participants : <span data-count>1</span>
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>750€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="accommodation">
                        <div class="gf-sejour-summary__label">
                            Hébergements : <span data-name></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-adult">
                        <div class="gf-sejour-summary__label">
                            Pension complète adulte
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-child">
                        <div class="gf-sejour-summary__label">
                            Pension complète enfant
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="transfer">
                        <div class="gf-sejour-summary__label">
                            Transfert depuis la gare
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                </div>
                <div class="gf-sejour-summary__total">
                    <div class="gf-sejour-summary__total-label">Total</div>
                    <div class="gf-sejour-summary__total-value" data-total>750€</div>
                </div>
                <div class="gf-sejour-summary__divider"></div>
                <div class="gf-sejour-summary__deposit">
                    <div class="gf-sejour-summary__deposit-label">Dû à la réservation</div>
                    <div class="gf-sejour-summary__deposit-value" data-deposit>375€</div>
                </div>
            </div>
        `;

        const pages = this.formWrapper.querySelectorAll('.gform_page');
        if (pages.length > 0) {
            pages.forEach(page => {
                const footer = page.querySelector('.gform_page_footer');
                if (footer) {
                    footer.insertAdjacentHTML('beforebegin', summaryHTML);
                }
            });
        } else {
            const footer = this.formWrapper.querySelector('.gform_footer');
            if (footer) {
                footer.insertAdjacentHTML('beforebegin', summaryHTML);
            }
        }
    }

    injectPaymentInfo() {
        const footers = this.formWrapper.querySelectorAll('.gform_page_footer, .gform_footer');
        footers.forEach(footer => {
            if (footer.querySelector('.gf-payment-info')) return;

            const paymentInfoHTML = `
                <div class="gf-payment-info">
                    <p>Paiement en <strong>2 fois sans frais</strong></p>
                    <p><strong>Annulation gratuite</strong> 30 jours avant le départ</p>
                </div>
            `;
            footer.insertAdjacentHTML('beforeend', paymentInfoHTML);
        });
    }

    enhanceAccommodationCards() {
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (!accommodationField) return;

        const labels = accommodationField.querySelectorAll('.gchoice label');
        labels.forEach(label => {
            // Vérifier si déjà transformé
            if (label.querySelector('.acc-radio-circle')) return;

            const accTitle = label.querySelector('.acc-title');
            if (accTitle) {
                // Créer le cercle radio
                const radioCircle = document.createElement('span');
                radioCircle.className = 'acc-radio-circle';

                // Insérer le cercle avant le titre
                accTitle.parentNode.insertBefore(radioCircle, accTitle);
            }
        });
    }

    bindEvents() {
        console.log('bindEvents called');

        this.formWrapper.addEventListener('change', (e) => {
            this.updateState();
        });

        this.formWrapper.addEventListener('input', (e) => {
            if (e.target.matches('input[type="number"]')) {
                this.updateState();
            }
        });

        // Écouter les clics sur les boutons Précédent et Suivant
        document.addEventListener('click', (e) => {
            const prevButton = e.target.closest('.gform_previous_button');
            const nextButton = e.target.closest('.gform_next_button');

            if (prevButton) {
                const pageBeforeClick = this.currentPage;
                console.log('Previous button clicked! Current page before:', pageBeforeClick);
                this.pendingBackNavigation = true;
                this.pageBeforeNavigation = pageBeforeClick;
                // Attendre que la page change puis reset (délai plus long pour AJAX)
                setTimeout(() => {
                    console.log('setTimeout fired for previous button');
                    this.checkAndResetAfterNavigation();
                }, 1000);
            }

            if (nextButton) {
                const pageBeforeClick = this.currentPage;
                console.log('Next button clicked! Current page before:', pageBeforeClick);
                this.pageBeforeNavigation = pageBeforeClick;
                // Attendre que la page change puis mettre à jour currentPage
                setTimeout(() => {
                    console.log('setTimeout fired for next button');
                    this.updateCurrentPageAfterNavigation();
                }, 1000);
            }
        }, true); // capture phase

        // Utiliser jQuery pour écouter les événements Gravity Forms
        if (typeof jQuery !== 'undefined') {
            console.log('jQuery found, binding gform_page_loaded');
            jQuery(document).on('gform_page_loaded', (event, formId, currentPage) => {
                console.log('jQuery gform_page_loaded - currentPage:', currentPage, 'previousPage:', this.currentPage);
                this.handlePageChange(currentPage);
            });
        }

        // MutationObserver comme fallback fiable pour détecter les changements de page
        this.setupPageObserver();
    }

    setupPageObserver() {
        const pages = this.formWrapper.querySelectorAll('.gform_page');
        console.log('Setting up MutationObserver for', pages.length, 'pages');

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const target = mutation.target;
                    if (target.classList.contains('gform_page')) {
                        // Une page a changé de visibilité
                        const newPage = this.getCurrentPageNumber();
                        if (newPage !== this.currentPage) {
                            console.log('MutationObserver detected page change:', this.currentPage, '->', newPage);
                            this.handlePageChange(newPage);
                        }
                    }
                }
            });
        });

        // Observer tous les éléments .gform_page
        pages.forEach(page => {
            observer.observe(page, { attributes: true, attributeFilter: ['style'] });
        });
    }

    handlePageChange(newPage) {
        console.log('handlePageChange called - newPage:', newPage, 'currentPage:', this.currentPage, 'pendingBack:', this.pendingBackNavigation);

        // Détecter si on revient en arrière
        if (newPage < this.currentPage || this.pendingBackNavigation) {
            if (newPage < this.currentPage) {
                console.log('Going back! Resetting steps after page', newPage);
                this.resetStepsAfter(newPage);
            }
        }

        this.pendingBackNavigation = false;
        this.currentPage = newPage;

        this.quantityControls.init();
        this.participantsManager.updateVisibility();
        this.enhanceAccommodationCards();
        this.updateState();
    }

    checkAndResetAfterNavigation() {
        const newPage = this.getCurrentPageNumber();
        const previousPage = this.pageBeforeNavigation || this.currentPage;
        console.log('checkAndResetAfterNavigation - newPage:', newPage, 'previousPage:', previousPage);

        if (newPage < previousPage) {
            console.log('Confirmed going back! Resetting steps after page', newPage);
            this.resetStepsAfter(newPage);
        }

        this.currentPage = newPage;
        this.pageBeforeNavigation = null;
        this.pendingBackNavigation = false;
        this.quantityControls.init();
        this.updateState();
    }

    updateCurrentPageAfterNavigation() {
        const newPage = this.getCurrentPageNumber();
        console.log('updateCurrentPageAfterNavigation - newPage:', newPage, 'previousPage:', this.currentPage);
        this.currentPage = newPage;
        this.quantityControls.init();
        this.participantsManager.updateVisibility();
        this.enhanceAccommodationCards();
        this.updateState();
    }

    getCurrentPageNumber() {
        // Essayer plusieurs méthodes pour trouver la page visible
        const pages = this.formWrapper.querySelectorAll('.gform_page');
        for (const page of pages) {
            const style = window.getComputedStyle(page);
            if (style.display !== 'none') {
                const pageId = page.id;
                const match = pageId.match(/gform_page_\d+_(\d+)/);
                if (match) {
                    return parseInt(match[1]);
                }
            }
        }
        return 1;
    }

    resetStepsAfter(pageNumber) {
        console.log('resetStepsAfter called with pageNumber:', pageNumber);

        // Reset étape 2 (hébergement) si on revient à l'étape 1
        if (pageNumber <= 1) {
            console.log('Resetting accommodation (step 2)');
            this.resetAccommodation();
        }

        // Reset étape 3 (pensions et transfert) si on revient à l'étape 1 ou 2
        if (pageNumber <= 2) {
            console.log('Resetting pensions and transfer (step 3)');
            this.resetPensions();
            this.resetTransfer();
        }

        // Mettre à jour le résumé
        this.updateState();
    }

    resetAccommodation() {
        // Décocher tous les hébergements et sélectionner le premier par défaut
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (accommodationField) {
            const inputs = accommodationField.querySelectorAll('input[type="radio"]');
            inputs.forEach((input, index) => {
                input.checked = (index === 0);
            });
        }

        this.state.accommodation = null;
        this.state.accommodationPrice = 0;
    }

    resetPensions() {
        console.log('Resetting pensions...');
        // Remettre les pensions à 0 via classes CSS custom
        const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');

        if (adultField) {
            const input = adultField.querySelector('.ginput_quantity');
            if (input) {
                console.log('Resetting adult pension input:', input);
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        if (childField) {
            const input = childField.querySelector('.ginput_quantity');
            if (input) {
                console.log('Resetting child pension input:', input);
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        this.state.adultPensions = 0;
        this.state.childPensions = 0;
    }

    resetTransfer() {
        console.log('Resetting transfer...');
        // Remettre le transfert à 0 via classe CSS custom
        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const input = transferField.querySelector('.ginput_quantity');
            if (input) {
                console.log('Resetting transfer input:', input);
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        this.state.transferCount = 0;
    }

    triggerInputChange(input) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateState() {
        this.readParticipants();
        this.readAccommodation();
        this.readPensions();
        this.readTransfer();
        this.summary.update();
    }

    readParticipants() {
        const participantField = this.formWrapper.querySelector('.gf-sejour-participants');
        if (participantField) {
            // Chercher l'input quantity (peut avoir été wrappé par QuantityControls)
            const qtyInput = participantField.querySelector('.ginput_quantity, .gf-quantity-input');
            if (qtyInput) {
                const value = parseInt(qtyInput.value) || 1;
                this.state.participants = Math.max(1, value);
                console.log('readParticipants - value:', value, 'state.participants:', this.state.participants);
            }
        }
    }

    readAccommodation() {
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (accommodationField) {
            const checkedInput = accommodationField.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
                const label = checkedInput.closest('.gchoice')?.querySelector('.acc-title');
                const value = checkedInput.value;
                this.state.accommodation = label ? label.textContent.trim() : value.split('|')[0];

                const pricePart = value.split('|')[1];
                this.state.accommodationPrice = pricePart ? parseFloat(pricePart) || 0 : 0;
            }
        }
    }

    readPensions() {
        const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');

        if (adultField) {
            const input = adultField.querySelector('.ginput_quantity');
            this.state.adultPensions = input ? parseInt(input.value) || 0 : 0;
        }
        if (childField) {
            const input = childField.querySelector('.ginput_quantity');
            this.state.childPensions = input ? parseInt(input.value) || 0 : 0;
        }
    }

    readTransfer() {
        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const input = transferField.querySelector('.ginput_quantity');
            this.state.transferCount = input ? parseInt(input.value) || 0 : 0;
        }
    }

    calculateTotal() {
        const participantsTotal = this.state.participants * this.prices.participant;
        const accommodationTotal = this.state.accommodationPrice;
        const adultPensionTotal = this.state.adultPensions * this.prices.adultPension;
        const childPensionTotal = this.state.childPensions * this.prices.childPension;
        const transferTotal = this.state.transferCount * this.prices.transfer;

        return participantsTotal + accommodationTotal + adultPensionTotal + childPensionTotal + transferTotal;
    }

    calculateDeposit() {
        return Math.round(this.calculateTotal() * this.config.depositPercentage);
    }

    formatPrice(amount) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new GFormSejour();
});

if (typeof gform !== 'undefined') {
    gform.addAction('gform_post_render', () => {
        new GFormSejour();
    });
}
